import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import uniq from 'lodash/uniq';

// Types
import { WidthType } from 'Wpds/components/dropdown';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Images
import ProfileImg from 'Shared/images/undraw_profile.svg';

// Components
import WpSelectV2 from 'Wpds/components/select/WpSelectV2';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpCheckbox } from 'Wpds/components/checkbox';
import { WpDropdown, WpDropdownMenu, WpDropdownToggle, WpDropdownMenuItem } from 'Wpds/components/dropdown';

// Hooks
import { useMembers } from 'Shared/utils/hooks';

// Styles
import './DepartmentFilter.scss';

type Props = {
  addFilter: Function;
  handleRemoveFilter: Function;
  selectedFilters: Array<any>;
};

const DepartmentFilter = ({ addFilter, handleRemoveFilter, selectedFilters }: Props) => {
  const history = useHistory();
  const { isLoading: isLoadingMembers } = useMembers({ refresh: true });
  const members = useSelector(state => state.members);

  // need to wait for members fetch to get users
  if (isLoadingMembers) return null;

  const departments = uniq(members.filter(member => member.department).map(member => member.department));

  const hadDepartments = departments.length > 0;

  const appliedFilters = selectedFilters.filter(filter => filter.type === 'department');
  const appliedFiltersLabels = appliedFilters.map(filter => filter.label);
  const hasFilters = appliedFilters.length > 0;

  const handleUpdateFilter = ({ label, value }) => {
    const isApplied = appliedFilters.find(filter => filter.departmentId === value);
    if (isApplied) {
      handleRemoveFilter(isApplied);
    } else {
      addFilter({ type: 'department', label: label, departmentId: value });
    }
  };

  const CustomInnerDropdown = () => {
    return (
      <WpButton variant={hasFilters ? 'primary' : 'tertiary'} size="sm" square slimPadding>
        Department {hasFilters && <>({appliedFilters.length})</>} <WpIcon name="angle-down" />
      </WpButton>
    );
  };

  const CustomOption = ({ option }) => {
    return (
      <WpCheckbox
        id={`department-filter-option-cb-${option.value}`}
        label={option.label}
        checked={appliedFiltersLabels.includes(option.label)}
      />
    );
  };

  const handleLaunchProfile = () => {
    history.push(PageUrls.CURRENT_USER_PROFILE_PAGE);
  };

  const baseClassName = 'DepartmentFilter';

  if (!hadDepartments) {
    return (
      <WpDropdown>
        <WpDropdownToggle>
          <CustomInnerDropdown />
        </WpDropdownToggle>
        <WpDropdownMenu>
          <WpDropdownMenuItem removeBackgroundHover onClick={handleLaunchProfile}>
            <div className={`${baseClassName}__empty`}>
              <img className={`${baseClassName}__empty__img`} src={ProfileImg} alt="Add your department" />

              <p className={`${baseClassName}__empty__text`}>Add your department to your user profile.</p>
              <WpButton size="sm">Add Department</WpButton>
            </div>
          </WpDropdownMenuItem>
        </WpDropdownMenu>
      </WpDropdown>
    );
  }

  return (
    <WpSelectV2
      id="department-filter"
      label="Department Filter"
      labelHidden
      options={departments}
      components={{ option: CustomOption, innerDropdown: CustomInnerDropdown }}
      onChange={handleUpdateFilter}
      closeOnClick={false}
      optionsWidth={WidthType.fitWidth}
    />
  );
};

export default DepartmentFilter;
